import { configApiRef, discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { Button, Chip, Theme, Tooltip, Typography, makeStyles, useTheme } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { Alert, AlertTitle } from '@material-ui/lab';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ANNOUNCEMENTS_PATH, Incident, announcementViewPath, fetchIncidents } from './homeAnnouncements';
import { readStatusConfig } from './homeConfig';
import { formatRelative, useAsyncValue } from './homeUtils';
import { ServiceStatus, StatusLevel, fetchServiceStatus } from './serviceStatus';

const LEVEL_LABELS: Record<StatusLevel, string> = {
  operational: 'Operational',
  degraded: 'Degraded',
  maintenance: 'Maintenance',
  outage: 'Outage',
  unknown: 'Unknown',
};

const levelColor = (theme: Theme, level: StatusLevel): string => {
  switch (level) {
    case 'operational':
      return theme.palette.success.main;
    case 'degraded':
    case 'maintenance':
      return theme.palette.warning.main;
    case 'outage':
      return theme.palette.error.main;
    default:
      return theme.palette.text.disabled;
  }
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  strip: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  label: {
    fontWeight: 700,
    letterSpacing: '0.08em',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5),
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    flex: 1,
  },
  chip: {
    fontWeight: 600,
  },
  // A plain 9px circle: MUI forces small chip icons to 18px, which made the dots look oversized.
  dot: {
    '&&': {
      display: 'inline-block',
      flexShrink: 0,
      width: 9,
      height: 9,
      borderRadius: '50%',
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(-0.25),
    },
  },
  updated: {
    whiteSpace: 'nowrap',
  },
  bannerAction: {
    whiteSpace: 'nowrap',
  },
}));

interface StatusChipProps {
  title: string;
  status?: ServiceStatus;
  href?: string;
  to?: string;
}

const StatusChip = ({ title, status, href, to }: StatusChipProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const level = status?.level ?? 'unknown';
  const color = levelColor(theme, level);
  const tooltip = status ? `${LEVEL_LABELS[level]} - ${status.message}` : 'Checking status...';
  const common = {
    size: 'small' as const,
    variant: 'outlined' as const,
    clickable: true,
    label: title,
    className: classes.chip,
    icon: <span className={classes.dot} style={{ backgroundColor: color }} aria-hidden="true" />,
    style: { borderColor: alpha(color, 0.5), backgroundColor: alpha(color, 0.08) },
    'aria-label': `${title}: ${LEVEL_LABELS[level]}`,
  };

  return (
    <Tooltip title={tooltip} arrow>
      {to ? (
        <Chip {...common} component={RouterLink} to={to} />
      ) : (
        <Chip {...common} component="a" href={href} target="_blank" rel="noopener noreferrer" />
      )}
    </Tooltip>
  );
};

const bannerTitle = (incident: Incident): string => {
  if (incident.kind === 'outage') return 'Service outage';
  return incident.upcoming ? 'Scheduled maintenance' : 'Maintenance in progress';
};

const IncidentBanner = ({ incident }: { incident: Incident }) => {
  const classes = useStyles();
  const { announcement, kind, upcoming } = incident;
  const start = announcement.start_at || announcement.created_at;

  return (
    <Alert
      severity={kind === 'outage' ? 'error' : 'warning'}
      variant={kind === 'outage' ? 'filled' : 'standard'}
      action={
        <Button
          color="inherit"
          size="small"
          component={RouterLink}
          to={announcementViewPath(announcement.id)}
          className={classes.bannerAction}
        >
          Details
        </Button>
      }
    >
      <AlertTitle>
        {bannerTitle(incident)}: {announcement.title}
      </AlertTitle>
      {announcement.excerpt}
      {announcement.excerpt ? ' - ' : ''}
      {upcoming ? 'Starts' : 'Started'} {formatRelative(start)}
    </Alert>
  );
};

/**
 * Service status strip shown above the home page hero: one chip per external
 * vendor status page (through the backend proxy) plus an "Internal services"
 * chip and banners driven by Outage/Maintenance announcements. Every source
 * fails soft to a grey "unknown" chip.
 */
export const ServiceStatusStrip = () => {
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const config = useMemo(() => readStatusConfig(configApi), [configApi]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), config.refreshMinutes * 60 * 1000);
    return () => clearInterval(timer);
  }, [config.refreshMinutes]);

  const external = useAsyncValue(async () => {
    if (!config.enabled) return { statuses: [] as ServiceStatus[], checkedAt: Date.now() };
    const statuses = await Promise.all(
      config.sources.map(source => fetchServiceStatus(source, discoveryApi, fetchApi)),
    );
    return { statuses, checkedAt: Date.now() };
  }, [tick, config, discoveryApi, fetchApi]);

  const incidents = useAsyncValue(
    () => fetchIncidents(discoveryApi, fetchApi, config.incidents),
    [tick, config, discoveryApi, fetchApi],
  );

  const internalStatus = useMemo<ServiceStatus | undefined>(() => {
    if (incidents.error) return { level: 'unknown', message: 'Announcements unavailable right now' };
    if (!incidents.value) return undefined;
    const current = incidents.value.filter(i => !i.upcoming);
    const outages = current.filter(i => i.kind === 'outage').length;
    const maintenance = current.filter(i => i.kind === 'maintenance').length;
    if (outages > 0) return { level: 'outage', message: `${outages} active outage${outages === 1 ? '' : 's'}` };
    if (maintenance > 0) return { level: 'maintenance', message: 'Maintenance in progress' };
    return { level: 'operational', message: 'No reported internal incidents' };
  }, [incidents.value, incidents.error]);

  const banners = [...(incidents.value ?? [])].sort((a, b) => {
    const rank = (i: Incident) => (i.kind === 'outage' ? 0 : 1) + (i.upcoming ? 2 : 0);
    return rank(a) - rank(b);
  });

  return (
    <div className={classes.root}>
      {config.enabled && (
        <div className={classes.strip} role="region" aria-label="Service status">
          <Typography variant="overline" className={classes.label}>
            Service status
          </Typography>
          <div className={classes.chips}>
            <StatusChip title="Internal services" status={internalStatus} to={ANNOUNCEMENTS_PATH} />
            {config.sources.map((source, index) => (
              <StatusChip
                key={source.id}
                title={source.title}
                status={external.value?.statuses[index]}
                href={source.link}
              />
            ))}
          </div>
          {external.value && (
            <Typography variant="caption" color="textSecondary" className={classes.updated}>
              Checked{' '}
              {new Date(external.value.checkedAt).toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Typography>
          )}
        </div>
      )}
      {banners.map(incident => (
        <IncidentBanner key={incident.announcement.id} incident={incident} />
      ))}
    </div>
  );
};
